/**
 * CR-6685: Company Name typeahead for the Add Approved Supplier modal.
 *
 * As the user types, it searches existing approved suppliers within their
 * organisation (org-scoped server-side) and shows matches — with the supplier's
 * logo — in a dropdown. Picking a match calls ``onSelectExisting`` so the caller
 * can auto-populate and link the existing supplier instead of creating a
 * duplicate. Free text with no selection continues creating a new supplier.
 */
import { useEffect, useRef, useState } from 'react';
import { Input } from '../../components/ui/input';
import { Building2, Loader2 } from 'lucide-react';
import { suppliersAPI, ExistingSupplierMatch } from '../../lib/api';

interface SupplierNameSearchProps {
  value: string;
  /** Free-text change (user typing a name that isn't a selected existing supplier). */
  onChange: (value: string) => void;
  /** The user picked an existing supplier from the results. */
  onSelectExisting: (match: ExistingSupplierMatch) => void;
  placeholder?: string;
  /** Shown when nothing matches; the default says a new supplier will be created. */
  noMatchText?: string;
}

export default function SupplierNameSearch({
  value, onChange, onSelectExisting, placeholder,
  noMatchText = 'No existing supplier found — a new one will be created.',
}: SupplierNameSearchProps) {
  const [matches, setMatches] = useState<ExistingSupplierMatch[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  // A programmatic value change (right after a pick) must not re-trigger a search.
  const skipNext = useRef(false);

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    const q = value.trim();
    if (q.length < 2) { setMatches([]); setSearching(false); return; }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      suppliersAPI.searchExistingSuppliers(q)
        .then((res) => { if (!cancelled) setMatches(res.data.results || []); })
        .catch(() => { if (!cancelled) setMatches([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [value]);

  const pick = (match: ExistingSupplierMatch) => {
    skipNext.current = true;
    setOpen(false);
    setMatches([]);
    onSelectExisting(match);
  };

  const showDropdown = open && value.trim().length >= 2;

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => { if (matches.length) setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {showDropdown && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover shadow-md">
          {searching && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
            </div>
          )}
          {!searching && matches.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {noMatchText}
            </div>
          )}
          {matches.map((m) => {
            const sub = [m.supplier_code, [m.city, m.country].filter(Boolean).join(', ')]
              .filter(Boolean).join(' · ');
            return (
              <button
                key={m.supplier_id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(m)}
                className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded border bg-background">
                  {m.logo_url
                    ? <img src={m.logo_url} alt="" className="h-full w-full object-contain" />
                    : <Building2 className="h-4 w-4 text-muted-foreground" />}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{m.company_name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{sub || 'Existing supplier'}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
